import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Column } from "primereact/column";
import env from "@beam-australia/react-env";
import jwtDecode from "jwt-decode";
import { Toast } from "primereact/toast";
import useCRUDService from "../../hooks/useCRUDService";
import useApi from "../../hooks/useApi";
import Searcher from "../searcher/searcher";

const Topbar = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const api = useApi();
    const CRUDService = useCRUDService("clients");
    const toast = useRef(null);

    const normalizeText = (value) => {
        if (value === null || value === undefined) return "";
        const text = String(value).trim();
        if (!text) return "";
        const lower = text.toLowerCase();
        return lower === "undefined" || lower === "null" ? "" : text;
    };

    const clientName = normalizeText(localStorage.getItem("clientName"));
    const clientId = localStorage.getItem("clientId");
    const userRole = normalizeText(localStorage.getItem("userRole"));

    let decodedAccessToken = null;
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
        try {
            decodedAccessToken = jwtDecode(accessToken);
        } catch (error) {
            decodedAccessToken = null;
        }
    }

    const tokenPersonName = normalizeText(
        decodedAccessToken?.personName ||
        decodedAccessToken?.userName ||
        decodedAccessToken?.name ||
        decodedAccessToken?.nick ||
        decodedAccessToken?.preferred_username ||
        decodedAccessToken?.unique_name ||
        decodedAccessToken?.sub
    );

    const personName =
        normalizeText(localStorage.getItem("personName")) ||
        tokenPersonName ||
        clientName ||
        "Usuario";

    const [selectedClient, setSelectedClient] = useState({
        id: clientId,
        name: clientName,
    });

    const getInitials = (name) => {
        if (!name) return "U";
        const parts = name.trim().split(" ");
        return parts.map((p) => p[0]).slice(0, 2).join("").toUpperCase();
    };

    const fetchData = async (first, rows, sortField, sortOrder, debouncedSearchTerm) => {
        const columns = JSON.stringify([
            { name: "name", type: "string" },
            { name: "address", type: "string" },
            { name: "phone", type: "string" },
            { name: "notes", type: "string" },
        ]);
        return await CRUDService.getEntities(columns, first, rows, sortField, sortOrder, debouncedSearchTerm, "base");
    };

    const changeClient = async (value) => {
        try {
            const response = await api.post(
                `${env("API_URL")}/base/auth/changeclient`,
                {
                    AccessToken: localStorage.getItem("accessToken"),
                    RefreshToken: localStorage.getItem("refreshToken"),
                    clientId: value.id,
                }
            );

            const decodedToken = response.data.accessToken.token
                ? jwtDecode(response.data.accessToken.token)
                : null;

            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("clientId");
            localStorage.removeItem("clientName");
            localStorage.removeItem("userRole");
            localStorage.removeItem("personName");

            localStorage.setItem("accessToken", response.data.accessToken.token);
            localStorage.setItem("refreshToken", response.data.refreshToken);
            localStorage.setItem("clientId", decodedToken.clientId);
            localStorage.setItem("clientName", decodedToken.clientName);
            localStorage.setItem("userRole", decodedToken.userRole);
            localStorage.setItem(
                "personName",
                normalizeText(
                    decodedToken.personName ||
                    decodedToken.userName ||
                    decodedToken.name ||
                    decodedToken.nick ||
                    decodedToken.preferred_username ||
                    decodedToken.unique_name ||
                    decodedToken.sub
                )
            );

            setSelectedClient(value);

            toast.current.show({
                severity: "success",
                summary: t("forms.messages.titles.success"),
                detail: t("forms.messages.operation.success"),
                life: 3000,
            });

            if (window.location.pathname === "/crud/clients") {
                window.location.reload();
            } else {
                navigate("/crud/clients");
            }
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: t("forms.messages.titles.error"),
                detail: t("forms.messages.operation.error"),
                life: 3000,
            });
        }
    };

    return (
        <div className="app-topbar">
            <Toast ref={toast} />
            <div className="topbar-left">
                {userRole === "SuperUsers" && (
                    <div className="flex align-items-center gap-2">
                        <span style={{ fontSize: "12px", color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>
                            {t("entities.clients.singular")}:
                        </span>
                        <InputText
                            value={selectedClient.name || ""}
                            readOnly
                            style={{ width: "160px", height: "34px", fontSize: "13px" }}
                        />
                        <Searcher
                            settings={{
                                entityName: "clients",
                                fieldName: "clients",
                                selectedEntity: selectedClient,
                                setSelectedEntity: changeClient,
                                defaultSortField: "name",
                                fetchData: fetchData,
                                columns: [
                                    <Column field="name" header={t("entities.clients.name")} sortable key="name" />,
                                    <Column field="address" header={t("entities.clients.address")} sortable key="address" />,
                                    <Column field="phone" header={t("entities.clients.phone")} sortable key="phone" />,
                                    <Column field="notes" header={t("entities.clients.notes")} sortable key="notes" />,
                                ],
                            }}
                        />
                    </div>
                )}
            </div>

            <div className="topbar-right">
                <div className="topbar-user">
                    <div className="topbar-avatar">{getInitials(personName)}</div>
                    <div className="topbar-user-info">
                        <div className="topbar-user-name">{personName}</div>
                        <div className="topbar-user-role">{userRole}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Topbar;
